import React, { useState, useCallback, useMemo } from 'react';
import { useUser, useFindMany, useGlobalAction, useAction } from '@gadgetinc/react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '../api';
import { hasAnyRole, normalizeRoleKey } from '../lib/roleUtils';

// Helper functions for safe array operations
const safeArrayConversion = (value, fallback = []) => {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : fallback;
    } catch (e) {
      return fallback;
    }
  }
  return fallback;
};

const safeArrayMap = (array, callback, fallback = []) => {
  const safeArray = safeArrayConversion(array, fallback);
  return safeArray.map(callback);
};

const safeArrayFilter = (array, callback, fallback = []) => {
  const safeArray = safeArrayConversion(array, fallback);
  return safeArray.filter(callback);
};

const ACCESS_TIER_OPTIONS = [
  { value: "", label: "No direct tier (cohort/manual only)" },
  { value: "student_individual", label: "Student (Individual)" },
  { value: "student_institutional", label: "Student (Institutional)" },
  { value: "instructor_individual", label: "Instructor (Individual)" },
  { value: "instructor_institutional", label: "Instructor (Institutional)" },
];

const tierGrantsScenarioGenerator = (tier) => {
  return tier === "instructor_individual" || tier === "instructor_institutional";
};

const parseTierGrant = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  if (!value.tier) return null;
  return {
    tier: value.tier,
    source: value.source || "manual",
    addons: safeArrayConversion(value.addons, []),
    startAt: value.startAt || null,
    endAt: value.endAt || null,
  };
};

export default function AdminUsers() {
  const user = useUser();
  const navigate = useNavigate();
  
  // Authentication guard
  const isAdmin = hasAnyRole(user?.roles, ['admin']) || user?.email === '74spartan@gmail.com';
  
  if (!user) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#111111',
        color: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '18px'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (!isAdmin) {
    navigate('/sign-in');
    return (
      <div style={{
        minHeight: '100vh',
        background: '#111111',
        color: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '18px'
      }}>
        <div>Redirecting...</div>
      </div>
    );
  }

  return <AdminUsersContent />;
}

function AdminUsersContent() {
  // Get current user for operations that require it
  const user = useUser();
  
  // State management
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    approvalStatus: '',
    institution: ''
  });
  const [sortConfig, setSortConfig] = useState({
    field: 'createdAt',
    direction: 'Descending'
  });
  const [currentPage, setCurrentPage] = useState({ cursor: undefined, direction: 'forward' });
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [notification, setNotification] = useState(null);
  const [actionBusy, setActionBusy] = useState({});
  const [showUserModal, setShowUserModal] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [customResetMessage, setCustomResetMessage] = useState('');
  const [showBulkCohortModal, setShowBulkCohortModal] = useState(false);
  const [bulkCohortForm, setBulkCohortForm] = useState({
    cohortId: '',
    institutionRole: 'student'
  });
  const [bulkCohortSubmitting, setBulkCohortSubmitting] = useState(false);
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    approvalStatus: '',
    institutions: [],
    newInstitution: '',
    cohortId: '',
    newCohort: '',
    institutionRole: [],
    assistantAccessMode: 'none',
    accessibleAssistantIds: [],
    selectedAgents: [],
    adminNotes: '',
    hasScenarioGeneratorAccess: false,
    hasCohortScenarioAccess: false,
    hasQuickQaEmailAccess: false,
    accessTier: "",
    accessSource: "manual",
    accessAddons: [],
    tierStartAt: null,
    tierEndAt: null
  });

  // Cohort creation form state
  const [showCreateCohort, setShowCreateCohort] = useState(false);
  const [createCohortForm, setCreateCohortForm] = useState({
    name: '',
    displayName: '',
    cohortType: 'EMT_Basic',
    academicYear: '',
    startDate: '',
    endDate: '',
    description: ''
  });
  
  const [availableAgents] = useState([
    {
      type: 'don',
      name: 'Don - Medical Assistant',
      description: 'Specialized medical assistant for clinical guidance and patient care',
      color: '#3b82f6'
    },
    {
      type: 'maia',
      name: 'Maia - Administrative Assistant',
      description: 'Administrative assistant for scheduling, documentation and workflow management',
      color: '#8b5cf6'
    },
    {
      type: 'gerald',
      name: 'Gerald - Educational Assistant',
      description: 'Educational assistant for learning support and training materials',
      color: '#10b981'
    }
  ]);
  
  const [institutionRoleOptions] = useState([
    { value: 'student', label: 'Student' },
    { value: 'instructor', label: 'Instructor' },
    { value: 'administrator', label: 'Administrator' },
    { value: 'staff', label: 'Staff' },
    { value: 'scheduler', label: 'Scheduler' }
  ]);

  // Build filter object for API call
  const buildFilter = useCallback(() => {
    const filter = {};
    
    if (filters.approvalStatus) {
      filter.approvalStatus = { equals: filters.approvalStatus };
    }

    if (filters.institution) {
      filter.institution = { equals: filters.institution };
    }

    return Object.keys(filter).length > 0 ? filter : undefined;
  }, [filters]);

  // Build sort object for API call
  const buildSort = useCallback(() => {
    return { [sortConfig.field]: sortConfig.direction };
  }, [sortConfig]);

  // Data fetching
  const [{ data: users, fetching, error }, refetch] = useFindMany(api.user, {
    select: {
      id: true,
      email: true,
      firstName: true,
      lastName: true,
      approvalStatus: true,
      approvedAt: true,
      registrationCompletedAt: true,
      lastSignedIn: true,
      createdAt: true,
      roles: { key: true, name: true },
      adminNotes: { markdown: true },
      assistantAccessMode: true,
      accessibleAssistantIds: true,
      agentAccessMode: true,
      accessibleAgentNames: true,
      agentAccessNotes: true,
      institution: true,
      institutionRole: true,
      cohort: { id: true, name: true, displayName: true },
      cohortId: true,
      activeCohorts: true,
      individualScenarioAccess: true,
      individualScenarioGeneratorAccess: true,
      documentProcessingAccess: true,
      individualAccessGrants: true,
      scenarioCredits: true,
      scenarioCreditsResetDate: true,
      scenariosUsedThisYear: true,
      accountType: true,
      parentInstitution: true,
      unlimitedScenarios: true,
      subscriptionStatus: true,
      subscriptionExpiration: true,
      cohortMemberships: {
        edges: {
          node: {
            id: true,
            hasScenarioGeneratorAccess: true,
            active: true,
            cohort: {
              id: true,
              name: true
            }
          }
        }
      }
    },
    filter: buildFilter(),
    sort: buildSort(),
    search: searchQuery || undefined,
    first: 25,
    after: currentPage.direction === 'forward' ? currentPage.cursor : undefined,
    before: currentPage.direction === 'backward' ? currentPage.cursor : undefined,
  });

  // Fetch all assistants for the access management
  const [{ data: assistants }] = useFindMany(api.assistant, {
    select: {
      id: true,
      name: true,
      description: true,
      openAiId: true
    },
    first: 250
  });

  // Fetch cohorts based on selected institution
  const [{ data: cohorts, fetching: cohortsFetching }, refetchCohorts] = useFindMany(api.cohort, {
    select: {
      id: true,
      name: true,
      displayName: true,
      institution: true,
      cohortType: true,
      isActive: true
    },
    filter: editForm.institutions[0] || editForm.newInstitution ? { 
      institution: { equals: editForm.institutions[0] || editForm.newInstitution },
      isActive: { equals: true }
    } : undefined,
    first: 250
  });

  const [{ data: allActiveCohorts, fetching: allCohortsFetching }] = useFindMany(api.cohort, {
    select: {
      id: true,
      name: true,
      displayName: true,
      institution: true,
      cohortType: true,
      isActive: true
    },
    filter: { isActive: { equals: true } },
    first: 250
  });

  // Get available institutions from users and cohorts
  const availableInstitutions = React.useMemo(() => {
    const institutions = new Set();
    
    const safeUsers = safeArrayConversion(users, []);
    const safeCohorts = safeArrayConversion(cohorts, []);
    
    safeUsers.forEach(user => {
      if (user?.institution) {
        institutions.add(user.institution);
      }
    });
    
    safeCohorts.forEach(cohort => {
      if (cohort?.institution) {
        institutions.add(cohort.institution);
      }
    });
    
    return Array.from(institutions).sort();
  }, [users, cohorts]);

  // Calculate pending users for the approval alert
  const pendingUsers = React.useMemo(() => {
    const safeUsers = safeArrayConversion(users, []);
    return safeUsers.filter(user => user?.approvalStatus === 'pending');
  }, [users]);

  // Global actions
  const [, approveUser] = useGlobalAction(api.approveUser);
  const [, denyUser] = useGlobalAction(api.denyUser);
  const [, resetPassword] = useGlobalAction(api.adminResetUserPassword);
  const [, generateTempPassword] = useGlobalAction(api.generateTempPassword);
  const [, giveAllAccess] = useGlobalAction(api.giveUserAllAssistantAccess);
  const [, resendPasswordSetupEmail] = useGlobalAction(api.resendPasswordSetupEmail);
  const [, updateUser] = useAction(api.user.update);
  const [, grantAgentAccess] = useGlobalAction(api.grantAgentAccess);
  const [{ data: updateRoleData, error: updateRoleError }, updateUserRoleAndCohort] = useGlobalAction(api.updateUserRoleAndCohort);
  const [, deleteUser] = useAction(api.user.delete);
  const [{ data: createCohortResult, fetching: creatingCohort, error: createCohortError }, createCohort] = useAction(api.cohort.create);
  const [, updateScenarioAccess] = useGlobalAction(api.updateUserScenarioAccess);
  const [, assignAccessTier] = useGlobalAction(api.assignAccessTier);
  const [{ data: resetData, fetching: resetting, error: resetError }, bulkPasswordReset] = useGlobalAction(api.bulkPasswordReset);

  // Helper functions
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const getActionPayload = (result, actionName) => {
    return result?.data?.[actionName] || result?.data || result;
  };

  const isPendingPasswordSetup = (targetUser) => {
    return targetUser?.approvalStatus === 'approved' && !targetUser?.registrationCompletedAt;
  };

  const isActiveApprovedUser = (targetUser) => {
    return targetUser?.approvalStatus === 'approved' && !!targetUser?.registrationCompletedAt;
  };

  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'Ascending' ? 'Descending' : 'Ascending'
    }));
    setCurrentPage({ cursor: undefined, direction: 'forward' });
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
    setCurrentPage({ cursor: undefined, direction: 'forward' });
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    setCurrentPage({ cursor: undefined, direction: 'forward' });
  };

  const handleSelectUser = (userId, checked) => {
    const newSelected = new Set(selectedUsers);
    if (checked) {
      newSelected.add(userId);
    } else {
      newSelected.delete(userId);
    }
    setSelectedUsers(newSelected);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const safeUsers = safeArrayConversion(users, []);
      setSelectedUsers(new Set(safeArrayMap(safeUsers, user => user?.id).filter(Boolean)));
    } else {
      setSelectedUsers(new Set());
    }
  };

  const handleUserAction = async (action, userId, additionalData = {}) => {
    const busyKey = `${action}:${userId}`;
    setActionBusy(prev => ({ ...prev, [busyKey]: true }));
    try {
      switch (action) {
        case 'approve':
          await approveUser({ userId });
          showNotification('User approved successfully');
          break;
        case 'deny':
          await denyUser({ userId });
          showNotification('User denied successfully');
          break;
        case 'delete':
          await deleteUser({ id: userId });
          showNotification('User deleted successfully');
          break;
        case 'resetPassword':
          const resetResult = await resetPassword({ userId });
          const resetPayload = getActionPayload(resetResult, 'adminResetUserPassword');
          if (resetPayload?.success === false) {
            showNotification(resetPayload.message || 'Password reset email was not sent', 'error');
          } else {
            showNotification(resetPayload?.message || `Password reset email sent to ${additionalData.email || 'user'}`);
          }
          break;
        case 'generateTempPassword':
          const result = await generateTempPassword({ userId });
          showNotification(`Temporary password generated: ${result.tempPassword}`);
          break;
        case 'giveAllAccess':
          await giveAllAccess({ userId });
          showNotification('All assistant access granted');
          break;
        case 'sendPasswordSetup':
          const setupResult = await resendPasswordSetupEmail({ email: additionalData.email });
          const setupPayload = getActionPayload(setupResult, 'resendPasswordSetupEmail');
          if (setupPayload?.success === false) {
            showNotification(setupPayload.error || setupPayload.message || 'Password setup email was not sent', 'error');
          } else {
            showNotification(setupPayload?.message || 'Password setup email sent successfully');
          }
          break;
        default:
          break;
      }
      refetch();
    } catch (err) {
      showNotification(`Error: ${err.message}`, 'error');
    } finally {
      setActionBusy(prev => {
        const next = { ...prev };
        delete next[busyKey];
        return next;
      });
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedUsers.size === 0) {
      showNotification('No users selected', 'error');
      return;
    }

    try {
      const userIds = Array.from(selectedUsers);
      
      switch (action) {
        case 'approve':
          for (const userId of userIds) {
            // Use updateUserRoleAndCohort to just change approval status
            // This will auto-assign the 'User' role thanks to the fix we just made
            await updateUserRoleAndCohort({ 
              userId, 
              approvalStatus: 'approved'
            });
          }
          showNotification(`${userIds.length} users approved successfully`);
          break;
        case 'deny':
          for (const userId of userIds) {
            // Use updateUserRoleAndCohort to just change approval status to denied
            await updateUserRoleAndCohort({ 
              userId, 
              approvalStatus: 'denied'
            });
          }
          showNotification(`${userIds.length} users denied successfully`);
          break;
        case 'bulkPasswordReset':
          // This is now handled by the modal confirmation
          break;
        case 'export':
          const safeUsers = safeArrayConversion(users, []);
          const filteredUsers = safeArrayFilter(safeUsers, user => user?.id && selectedUsers.has(user.id));
          const csvData = safeArrayMap(filteredUsers, user => [
            user?.email || '',
            user?.firstName || '',
            user?.lastName || '',
            user?.approvalStatus || '',
            user?.createdAt || '',
            user?.lastSignedIn || '',
          ].join(',')).join('\n');
            
          const csvContent = 'Email,First Name,Last Name,Approval Status,Created At,Last Signed In\n' + csvData;
          const blob = new Blob([csvContent], { type: 'text/csv' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'users-export.csv';
          a.click();
          window.URL.revokeObjectURL(url);
          showNotification('Users exported successfully');
          break;
        default:
          break;
      }
      
      setSelectedUsers(new Set());
      refetch();
    } catch (err) {
      showNotification(`Bulk action error: ${err.message}`, 'error');
    }
  };

  const handleBulkPasswordResetConfirm = async () => {
    try {
      const userIds = Array.from(selectedUsers);
      
      const result = await bulkPasswordReset({
        userIds,
        customMessage: customResetMessage || undefined
      });

      if (resetError) {
        showNotification(`Failed to send password reset emails: ${resetError.message}`, 'error');
      } else if (result) {
        const successCount = result.successCount || 0;
        const failureCount = result.failureCount || 0;
        
        if (failureCount === 0) {
          showNotification(`Sent password reset emails to ${successCount} users`);
        } else if (successCount > 0) {
          showNotification(`Sent to ${successCount} users, ${failureCount} failures. See console for details.`, 'error');
          console.error('Bulk password reset failures:', result.failures);
        } else {
          showNotification('Failed to send password reset emails', 'error');
        }
      }

      setShowPasswordResetModal(false);
      setCustomResetMessage('');
      setSelectedUsers(new Set());
      refetch();
    } catch (err) {
      showNotification(`Error sending password reset emails: ${err.message}`, 'error');
      console.error('Bulk password reset error:', err);
    }
  };

  const handleBulkAddToCohort = async () => {
    if (selectedUsers.size === 0) {
      showNotification('No users selected', 'error');
      return;
    }

    if (!bulkCohortForm.cohortId) {
      showNotification('Please select a cohort', 'error');
      return;
    }

    setBulkCohortSubmitting(true);

    try {
      const userIds = Array.from(selectedUsers);
      let successCount = 0;
      const failures = [];

      for (const userId of userIds) {
        try {
          await updateUserRoleAndCohort({
            userId,
            cohortId: bulkCohortForm.cohortId,
            institutionRole: bulkCohortForm.institutionRole
          });
          successCount += 1;
        } catch (error) {
          failures.push({
            userId,
            message: error?.message || 'Unknown error'
          });
        }
      }

      if (failures.length === 0) {
        showNotification(`Added ${successCount} user${successCount === 1 ? '' : 's'} to cohort`);
      } else if (successCount > 0) {
        const firstFailure = failures[0];
        showNotification(
          `Added ${successCount} users, ${failures.length} failed. First error: ${firstFailure.message}`,
          'error'
        );
        console.error('Bulk cohort add failures:', failures);
      } else {
        showNotification(`Failed to add users to cohort: ${failures[0]?.message || 'Unknown error'}`, 'error');
      }

      await refetch();

      if (successCount > 0) {
        setSelectedUsers(new Set());
        setShowBulkCohortModal(false);
        setBulkCohortForm({
          cohortId: '',
          institutionRole: 'student'
        });
      }
    } finally {
      setBulkCohortSubmitting(false);
    }
  };

  const confirmActionHandler = (action, userId = null) => {
    setConfirmAction({ action, userId });
    setShowConfirmDialog(true);
  };

  const executeConfirmedAction = async () => {
    if (confirmAction) {
      if (confirmAction.userId) {
        await handleUserAction(confirmAction.action, confirmAction.userId, confirmAction.additionalData);
      } else {
        await handleBulkAction(confirmAction.action);
      }
    }
    setShowConfirmDialog(false);
    setConfirmAction(null);
  };

  const openEditModal = async (user) => {
    try {
      const parsedAssistantIds = parseAssistantIds(user.accessibleAssistantIds);
      const parsedInstitutionRole = parseInstitutionRole(user.institutionRole);
      const userInstitutions = parseUserInstitutions(user.institution);
      
      // Check cohort-based access
      const hasCohortScenarioAccess = user?.cohortMemberships?.edges?.some(edge => 
        edge?.node?.active === true && edge?.node?.hasScenarioGeneratorAccess === true
      ) ?? false;
      
      // Check individual access grant
      const hasIndividualAccess =
        user?.individualScenarioAccess === true ||
        user?.individualScenarioGeneratorAccess === true;
      const tierGrant = parseTierGrant(user?.individualAccessGrants);
      
      setSelectedUser(user);
      setEditForm({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        approvalStatus: user.approvalStatus || 'pending',
        institutions: userInstitutions,
        newInstitution: '',
        cohortId: user.cohortId || '',
        newCohort: '',
        institutionRole: parsedInstitutionRole,
        assistantAccessMode: user.assistantAccessMode || 'none',
        accessibleAssistantIds: parsedAssistantIds,
        selectedAgents: [],
        adminNotes: user.adminNotes?.markdown || '',
        hasScenarioGeneratorAccess: hasIndividualAccess,
        hasCohortScenarioAccess: hasCohortScenarioAccess,
        hasQuickQaEmailAccess: user?.documentProcessingAccess === true,
        accessTier: tierGrant?.tier || "",
        accessSource: tierGrant?.source || "manual",
        accessAddons: tierGrant?.addons || [],
        tierStartAt: tierGrant?.startAt || null,
        tierEndAt: tierGrant?.endAt || null
      });
      
      setActiveTab('basic');
      setShowEditModal(true);
    } catch (err) {
      console.error('Error opening edit modal:', err);
      showNotification(`Error opening edit modal: ${err.message}`, 'error');
    }
  };

  const handleAssistantToggle = (assistantId, isSelected) => {
    setEditForm(prev => {
      const currentIds = safeArrayConversion(prev.accessibleAssistantIds, []);
      const newIds = [...currentIds];
      if (isSelected) {
        if (!newIds.includes(assistantId)) {
          newIds.push(assistantId);
        }
      } else {
        const index = newIds.indexOf(assistantId);
        if (index > -1) {
          newIds.splice(index, 1);
        }
      }
      return {
        ...prev,
        accessibleAssistantIds: newIds
      };
    });
  };

  const handleAgentToggle = (agentType, isSelected) => {
    setEditForm(prev => {
      const currentAgents = safeArrayConversion(prev.selectedAgents, []);
      const newAgents = [...currentAgents];
      if (isSelected) {
        if (!newAgents.includes(agentType)) {
          newAgents.push(agentType);
        }
      } else {
        const index = newAgents.indexOf(agentType);
        if (index > -1) {
          newAgents.splice(index, 1);
        }
      }
      return {
        ...prev,
        selectedAgents: newAgents
      };
    });
  };

  const handleInstitutionRoleToggle = (role, isSelected) => {
    setEditForm(prev => {
      const currentRoles = safeArrayConversion(prev.institutionRole, []);
      const newRoles = [...currentRoles];
      if (isSelected) {
        if (!newRoles.includes(role)) {
          newRoles.push(role);
        }
      } else {
        const index = newRoles.indexOf(role);
        if (index > -1) {
          newRoles.splice(index, 1);
        }
      }
      return {
        ...prev,
        institutionRole: newRoles
      };
    });
  };

  const parseAssistantIds = (idsField) => {
    return safeArrayConversion(idsField, []);
  };

  const parseInstitutionRole = (roleField) => {
    if (Array.isArray(roleField)) {
      return roleField.length > 0 ? roleField : ['student'];
    }

    if (typeof roleField === 'string') {
      const trimmed = roleField.trim();
      if (!trimmed) return ['student'];

      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed.length > 0 ? parsed : ['student'];
        }
        if (typeof parsed === 'string' && parsed.trim()) {
          return [parsed.trim()];
        }
      } catch (e) {
        return [trimmed];
      }
      return ['student'];
    }

    return ['student'];
  };

  const parseUserInstitutions = (institutionField) => {
    if (!institutionField) return [];
    if (Array.isArray(institutionField)) return institutionField;
    if (typeof institutionField === 'string') {
      try {
        const parsed = JSON.parse(institutionField);
        return Array.isArray(parsed) ? parsed : [institutionField];
      } catch (e) {
        return [institutionField];
      }
    }
    return [];
  };

  const getAssistantAccessSummary = (user) => {
    if (!user?.assistantAccessMode || user.assistantAccessMode === 'none') {
      return 'No access';
    }
    if (user.assistantAccessMode === 'all') {
      return 'All assistants';
    }
    if (user.assistantAccessMode === 'specific') {
      const ids = safeArrayConversion(user.accessibleAssistantIds, []);
      const count = ids.length;
      return `${count} assistant${count !== 1 ? 's' : ''}`;
    }
    return 'Unknown';
  };

  const getAgentAccessSummary = (user) => {
    if (!user?.agentAccessMode || user.agentAccessMode === 'none') {
      return 'No access';
    }
    if (user.agentAccessMode === 'all') {
      return 'All agents';
    }
    if (user.agentAccessMode === 'specific') {
      const names = safeArrayConversion(user.accessibleAgentNames, []);
      const count = names.length;
      return `${count} agent${count !== 1 ? 's' : ''}`;
    }
    return 'Unknown';
  };

  const getScenarioAccessSummary = (user) => {
    const hasIndividual =
      user?.individualScenarioAccess === true ||
      user?.individualScenarioGeneratorAccess === true;
    const hasCohort = user?.cohortMemberships?.edges?.some(e => 
      e?.node?.active && e?.node?.hasScenarioGeneratorAccess
    ) ?? false;
    
    if (hasIndividual && hasCohort) return 'Both';
    if (hasIndividual) return 'Individual';
    if (hasCohort) return 'Cohort';
    return 'None';
  };

  const handleCreateCohort = async () => {
    try {
      // Validate user is available
      if (!user || !user.id) {
        console.error('User not available for cohort creation:', user);
        showNotification('Unable to create cohort: User information not available. Please refresh the page and try again.', 'error');
        return;
      }

      // Validate required fields with specific messages
      const requiredFields = [];
      if (!createCohortForm.name.trim()) requiredFields.push('Cohort Name');
      if (!createCohortForm.academicYear.trim()) requiredFields.push('Academic Year');
      if (!createCohortForm.startDate) requiredFields.push('Start Date');
      if (!createCohortForm.endDate) requiredFields.push('End Date');

      if (requiredFields.length > 0) {
        showNotification(`Please fill in the following required fields: ${requiredFields.join(', ')}`, 'error');
        return;
      }

      // Validate date logic
      const startDate = new Date(createCohortForm.startDate);
      const endDate = new Date(createCohortForm.endDate);
      
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        showNotification('Please enter valid start and end dates', 'error');
        return;
      }

      if (endDate <= startDate) {
        showNotification('End date must be after start date', 'error');
        return;
      }

      // Validate institution
      const institutionToUse = editForm.newInstitution?.trim() || editForm.institutions[0];
      if (!institutionToUse) {
        showNotification('Please select an existing institution or enter a new institution name before creating a cohort', 'error');
        return;
      }

      console.log('Creating cohort with data:', {
        name: createCohortForm.name,
        institution: institutionToUse,
        userId: user.id,
        startDate: createCohortForm.startDate,
        endDate: createCohortForm.endDate
      });

      // Prepare cohort data
      const cohortData = {
        name: createCohortForm.name.trim(),
        displayName: createCohortForm.displayName?.trim() || createCohortForm.name.trim(),
        cohortType: createCohortForm.cohortType,
        academicYear: createCohortForm.academicYear.trim(),
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        institution: institutionToUse,
        isActive: true,
        createdBy: {
          _link: user.id
        }
      };

      if (createCohortForm.description?.trim()) {
        cohortData.description = {
          markdown: createCohortForm.description.trim()
        };
      }

      console.log('Final cohort data being sent:', cohortData);

      const result = await createCohort(cohortData);
      
      console.log('Create cohort result:', result);
      
      if (result.cohort) {
        showNotification(`Cohort "${result.cohort.name}" created successfully`);
        
        // Reset the create cohort form
        setCreateCohortForm({
          name: '',
          displayName: '',
          cohortType: 'EMT_Basic',
          academicYear: '',
          startDate: '',
          endDate: '',
          description: ''
        });
        
        // Hide the create cohort section
        setShowCreateCohort(false);
        
        // Select the newly created cohort
        setEditForm(prev => ({ ...prev, cohortId: result.cohort.id }));
        
        // Refresh the cohorts list
        await refetchCohorts();
      } else {
        console.error('Cohort creation returned no cohort:', result);
        showNotification('Cohort creation failed: No cohort data returned', 'error');
      }
    } catch (err) {
      console.error('Detailed error creating cohort:', {
        error: err,
        message: err.message,
        stack: err.stack,
        formData: createCohortForm,
        user: user
      });
      
      let errorMessage = 'Unknown error occurred';
      if (err.message) {
        errorMessage = err.message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      }
      
      showNotification(`Error creating cohort: ${errorMessage}. Please check the console for more details.`, 'error');
    }
  };

  const handleSaveUser = async () => {
    try {
      const scenarioAccessFromTier = tierGrantsScenarioGenerator(editForm.accessTier);
      const effectiveScenarioAccess = editForm.hasScenarioGeneratorAccess || scenarioAccessFromTier;

      // Update basic user information
      await updateUser({
        id: selectedUser.id,
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        assistantAccessMode: editForm.assistantAccessMode,
        accessibleAssistantIds: editForm.accessibleAssistantIds,
        adminNotes: {
          markdown: editForm.adminNotes || ''
        },
        individualScenarioAccess: effectiveScenarioAccess,
        individualScenarioGeneratorAccess: effectiveScenarioAccess,
        documentProcessingAccess: editForm.hasQuickQaEmailAccess === true
      });

      // Update role, cohort, and approval status
      const selectedInstitutionRoles = safeArrayConversion(editForm.institutionRole, []).filter(Boolean);
      if (selectedInstitutionRoles.length > 0 || editForm.cohortId || editForm.approvalStatus !== selectedUser.approvalStatus || editForm.institutions?.length > 0 || editForm.newInstitution) {
        const updateParams = {
          userId: selectedUser.id,
        };
        
        // Add institution if changed
        const institutionToUse = editForm.newInstitution?.trim() || editForm.institutions[0];
        if (institutionToUse) {
          updateParams.institution = institutionToUse;
        }
        
        if (selectedInstitutionRoles.length > 0) {
          updateParams.institutionRole = selectedInstitutionRoles[0];
        }
        
        if (editForm.cohortId) {
          updateParams.cohortId = editForm.cohortId;
        }
        
        if (editForm.approvalStatus && editForm.approvalStatus !== selectedUser.approvalStatus) {
          updateParams.approvalStatus = editForm.approvalStatus;
          console.log('Updating approval status to:', editForm.approvalStatus);
        }
        
        console.log('Calling updateUserRoleAndCohort with:', updateParams);
        await updateUserRoleAndCohort(updateParams);

        // Check the state variables for errors (useGlobalAction pattern)
        if (updateRoleError) {
          console.error('Failed to update user role and cohort:', updateRoleError);
          const errorMsg = typeof updateRoleError === 'string' 
            ? updateRoleError 
            : updateRoleError?.message || "Failed to update user";
          showNotification(errorMsg, 'error');
          return;
        }

        // Log the successful result from state
        if (updateRoleData) {
          console.log('Update user role and cohort result:', updateRoleData);
        }
      }

      // Update agent access
      const selectedAgents = safeArrayConversion(editForm.selectedAgents, []);
      if (selectedAgents.length > 0) {
        await grantAgentAccess({
          userEmail: selectedUser.email,
          agentTypes: selectedAgents,
          accessType: 'user_email'
        });
      }

      if (editForm.accessTier) {
        await assignAccessTier({
          userId: selectedUser.id,
          tier: editForm.accessTier,
          source: editForm.accessSource || "manual",
          addons: safeArrayConversion(editForm.accessAddons, []),
          startAt: editForm.tierStartAt || null,
          endAt: editForm.tierEndAt || null,
          syncAgentAccess: true,
          notes: "Updated from user management panel"
        });
      } else {
        await updateUser({
          id: selectedUser.id,
          individualAccessGrants: {}
        });
      }

      // Force refetch and wait for it to complete
      await refetch();

      // Show success message
      showNotification('User updated successfully');

      // Small delay to ensure UI updates before closing modal
      setTimeout(() => {
        setShowEditModal(false);
        setSelectedUser(null);
      }, 200);
    } catch (err) {
      console.error('Error saving user:', err);
      showNotification(`Error saving user: ${err.message}`, 'error');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return '#28a745';
      case 'denied': return '#dc3545';
      case 'pending': return '#ffc107';
      default: return '#6c757d';
    }
  };

  if (error) {
    return (
      <div style={{ 
        minHeight: '100vh',
        background: '#111111',
        color: '#ff4757',
        padding: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        Error loading users: {error.message}
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh',
      background: '#111111',
      color: '#ffffff',
      padding: '20px',
      maxWidth: '1400px',
      margin: '0 auto'
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <div>
          <h1 style={{ margin: '0 0 10px 0', fontSize: '28px', fontWeight: 'bold', color: '#ffffff' }}>User Management</h1>
          <p style={{ color: '#d1d5db', margin: 0 }}>Manage user accounts, permissions, and access levels</p>
        </div>
        <Link 
          to="/admin" 
          style={{ 
            background: '#374151', 
            color: '#ffffff', 
            padding: '0.5rem 1rem', 
            borderRadius: '6px', 
            textDecoration: 'none',
            fontSize: '0.9rem',
            fontWeight: '500',
            transition: 'background-color 0.2s',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            border: '1px solid #4b5563'
          }}
          onMouseOver={(e) => {
            e.target.style.backgroundColor = '#4b5563';
            e.target.style.borderColor = '#6b7280';
          }}
          onMouseOut={(e) => {
            e.target.style.backgroundColor = '#374151';
            e.target.style.borderColor = '#4b5563';
          }}
        >
          ← Back to Admin
        </Link>
      </div>

      {/* Pending Approvals Alert */}
      {pendingUsers && pendingUsers.length > 0 && (
        <div style={{
          padding: '20px',
          marginBottom: '25px',
          backgroundColor: '#1e1a0f',
          borderRadius: '8px',
          border: '2px solid #ffc107',
          boxShadow: '0 4px 6px rgba(255, 193, 7, 0.1)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                backgroundColor: '#ffc107',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px'
              }}>
                ⚠️
              </div>
              <div>
                <h3 style={{ margin: '0 0 5px 0', fontSize: '18px', fontWeight: 'bold', color: '#ffc107' }}>
                  Pending User Approvals
                </h3>
                <p style={{ margin: 0, color: '#d1d5db', fontSize: '14px' }}>
                  {pendingUsers.length} {pendingUsers.length === 1 ? 'user is' : 'users are'} waiting for approval
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button
                onClick={() => {
                  handleFilterChange('approvalStatus', 'pending');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: filters.approvalStatus === 'pending' ? '#6c757d' : '#ffc107',
                  color: filters.approvalStatus === 'pending' ? '#ffffff' : '#212529',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  if (filters.approvalStatus !== 'pending') {
                    e.target.style.backgroundColor = '#e0a800';
                  }
                }}
                onMouseOut={(e) => {
                  if (filters.approvalStatus !== 'pending') {
                    e.target.style.backgroundColor = '#ffc107';
                  }
                }}
              >
                <span>{filters.approvalStatus === 'pending' ? '✓ Showing Pending' : '👁️ Review Pending Users'}</span>
                <span style={{
                  padding: '2px 8px',
                  backgroundColor: filters.approvalStatus === 'pending' ? '#ffffff' : '#212529',
                  color: filters.approvalStatus === 'pending' ? '#212529' : '#ffc107',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}>
                  {pendingUsers.length}
                </span>
              </button>
              {filters.approvalStatus === 'pending' && (
                <button
                  onClick={() => {
                    handleSelectAll(true);
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  Select All Pending
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Notification */}
      {notification && (
        <div style={{
          padding: '12px 20px',
          marginBottom: '20px',
          borderRadius: '4px',
          backgroundColor: notification.type === 'error' ? '#2d1b1e' : '#1e2d1b',
          color: notification.type === 'error' ? '#ff4757' : '#10b981',
          border: `1px solid ${notification.type === 'error' ? '#ff4757' : '#10b981'}`
        }}>
          {notification.message}
        </div>
      )}

      {/* Controls */}
      <div style={{ 
        display: 'flex', 
        gap: '20px', 
        marginBottom: '20px', 
        padding: '20px',
        backgroundColor: filters.approvalStatus === 'pending' ? '#1e1a0f' : '#1e1e1e',
        borderRadius: '8px',
        flexWrap: 'wrap',
        alignItems: 'center',
        border: filters.approvalStatus === 'pending' ? '1px solid #ffc107' : '1px solid #333'
      }}>
        {/* Search */}
        <div style={{ flex: '1', minWidth: '200px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#ffffff' }}>Search</label>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search by email or name..."
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #333',
              borderRadius: '4px',
              fontSize: '14px',
              backgroundColor: '#2a2a2a',
              color: '#ffffff'
            }}
          />
        </div>

        {/* Approval Status Filter */}
        <div style={{ minWidth: '150px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#ffffff' }}>Approval Status</label>
          <select
            value={filters.approvalStatus}
            onChange={(e) => handleFilterChange('approvalStatus', e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: filters.approvalStatus === 'pending' ? '2px solid #ffc107' : '1px solid #333',
              borderRadius: '4px',
              fontSize: '14px',
              backgroundColor: filters.approvalStatus === 'pending' ? '#2a2200' : '#2a2a2a',
              color: '#ffffff',
              fontWeight: filters.approvalStatus === 'pending' ? 'bold' : 'normal'
            }}
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="denied">Denied</option>
          </select>
        </div>

        {/* Institution Filter */}
        <div style={{ minWidth: '150px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#ffffff' }}>Institution</label>
          <select
            value={filters.institution}
            onChange={(e) => handleFilterChange('institution', e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #333',
              borderRadius: '4px',
              fontSize: '14px',
              backgroundColor: '#2a2a2a',
              color: '#ffffff'
            }}
          >
            <option value="">All Institutions</option>
            {safeArrayMap(availableInstitutions, institution => (
              <option key={institution} value={institution}>{institution}</option>
            ))}
          </select>
        </div>

        {/* Clear Filters Button */}
        {(filters.approvalStatus || filters.institution || searchQuery) && (
          <div style={{ minWidth: '120px', alignSelf: 'flex-end' }}>
            <button
              onClick={() => {
                setFilters({ approvalStatus: '', institution: '' });
                setSearchQuery('');
                setCurrentPage({ cursor: undefined, direction: 'forward' });
              }}
              style={{
                width: '100%',
                padding: '8px 12px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* Bulk Actions */}
      {selectedUsers.size > 0 && (
        <div style={{
          padding: '15px 20px',
          marginBottom: '20px',
          backgroundColor: '#1e2d3a',
          borderRadius: '8px',
          border: '1px solid #3b82f6',
          display: 'flex',
          alignItems: 'center',
          gap: '15px',
          flexWrap: 'wrap'
        }}>
          <span style={{ fontWeight: 'bold', color: '#ffffff' }}>{selectedUsers.size} users selected</span>
          <button
            onClick={() => confirmActionHandler('approve')}
            style={{
              padding: '6px 12px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Bulk Approve
          </button>
          <button
            onClick={() => confirmActionHandler('deny')}
            style={{
              padding: '6px 12px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Bulk Deny
          </button>
          <button
            onClick={() => handleBulkAction('export')}
            style={{
              padding: '6px 12px',
              backgroundColor: '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Export Selected
          </button>
          <button
            onClick={() => setShowBulkCohortModal(true)}
            style={{
              padding: '6px 12px',
              backgroundColor: '#7c3aed',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Bulk Add to Cohort
          </button>
          <button
            onClick={() => setShowPasswordResetModal(true)}
            disabled={resetting}
            style={{
              padding: '6px 12px',
              backgroundColor: resetting ? '#6c757d' : '#fd7e14',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: resetting ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              opacity: resetting ? 0.6 : 1
            }}
          >
            {resetting ? 'Sending...' : 'Send Password Reset'}
          </button>
        </div>
      )}

      {/* Users Table */}
      <div style={{ 
        backgroundColor: filters.approvalStatus === 'pending' ? '#1e1a0f' : '#1e1e1e',
        borderRadius: '8px',
        border: filters.approvalStatus === 'pending' ? '2px solid #ffc107' : '1px solid #333',
        overflow: 'hidden',
        boxShadow: filters.approvalStatus === 'pending' ? '0 4px 6px rgba(255, 193, 7, 0.1)' : 'none'
      }}>
        {filters.approvalStatus === 'pending' && (
          <div style={{
            padding: '12px 20px',
            backgroundColor: '#2a2200',
            borderBottom: '1px solid #ffc107',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            color: '#ffc107',
            fontWeight: 'bold',
            fontSize: '14px'
          }}>
            <span>⚠️</span>
            <span>Showing Pending Users Only - Review and approve/deny below</span>
          </div>
        )}
        {fetching && <div style={{ padding: '20px', textAlign: 'center', color: '#ffffff' }}>Loading users...</div>}
        
        {!fetching && users && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ backgroundColor: '#252525' }}>
              <tr>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #333', color: '#ffffff' }}>
                  <input
                    type="checkbox"
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    checked={selectedUsers.size === safeArrayConversion(users, []).length && safeArrayConversion(users, []).length > 0}
                  />
                </th>
                <th 
                  style={{ 
                    padding: '12px', 
                    textAlign: 'left', 
                    borderBottom: '1px solid #333', 
                    cursor: 'pointer',
                    userSelect: 'none',
                    color: '#ffffff'
                  }}
                  onClick={() => handleSort('email')}
                >
                  Email {sortConfig.field === 'email' && (sortConfig.direction === 'Ascending' ? '52' : '50')}
                </th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #333', color: '#ffffff' }}>Name</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #333', color: '#ffffff' }}>Institution</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #333', color: '#ffffff' }}>Roles</th>
                <th 
                  style={{ 
                    padding: '12px', 
                    textAlign: 'left', 
                    borderBottom: '1px solid #333', 
                    cursor: 'pointer',
                    userSelect: 'none',
                    color: '#ffffff'
                  }}
                  onClick={() => handleSort('approvalStatus')}
                >
                  Status {sortConfig.field === 'approvalStatus' && (sortConfig.direction === 'Ascending' ? '52' : '50')}
                </th>
                <th 
                  style={{ 
                    padding: '12px', 
                    textAlign: 'left', 
                    borderBottom: '1px solid #333', 
                    cursor: 'pointer',
                    userSelect: 'none',
                    color: '#ffffff'
                  }}
                  onClick={() => handleSort('lastSignedIn')}
                >
                  Last Active {sortConfig.field === 'lastSignedIn' && (sortConfig.direction === 'Ascending' ? '52' : '50')}
                </th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #333', color: '#ffffff' }}>Assistant Access</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #333', color: '#ffffff' }}>Agent Access</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #333', color: '#ffffff' }}>Scenario Access</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #333', color: '#ffffff' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {safeArrayMap(users, (user) => (
                <tr key={user.id} style={{ borderBottom: '1px solid #333' }}>
                  <td style={{ padding: '12px' }}>
                    <input
                      type="checkbox"
                      checked={selectedUsers.has(user.id)}
                      onChange={(e) => handleSelectUser(user.id, e.target.checked)}
                    />
                  </td>
                  <td style={{ padding: '12px' }}>
                    <div style={{ 
                      fontWeight: hasAnyRole(user.roles, ['admin']) ? 'bold' : 'normal',
                      color: '#ffffff'
                    }}>
                      {user.email}
                    </div>
                  </td>
                  <td style={{ padding: '12px', color: '#ffffff' }}>
                    {[user.firstName, user.lastName].filter(Boolean).join(' ') || 'N/A'}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      {/* Institution Name */}
                      <div style={{ fontSize: '14px', fontWeight: '500' }}>
                        {user.institution ? (
                          <span style={{ color: '#ffffff' }}>{user.institution}</span>
                        ) : (
                          <span style={{ color: '#6b7280' }}>No institution</span>
                        )}
                      </div>
                      
                      {/* Institution Roles */}
                      {parseInstitutionRole(user.institutionRole).length > 0 && (
                        <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                          Role: {parseInstitutionRole(user.institutionRole).join(', ')}
                        </div>
                      )}
                      
                      {/* Cohort Information */}
                      {user.cohort && (
                        <div style={{ fontSize: '11px', color: '#3b82f6' }}>
                          Cohort: {user.cohort.displayName || user.cohort.name}
                          {user.cohort.cohortType && (
                            <span style={{ color: '#6b7280' }}> ({user.cohort.cohortType})</span>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '12px' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {safeArrayConversion(user?.roles, []).length > 0 ? safeArrayMap(user.roles, (role, index) => {
                        const roleKey = typeof role === 'string' ? role : role?.key || role?.name || 'unknown';
                        const displayName = typeof role === 'string' ? role : role?.name || role?.key || 'unknown';
                        const normalizedRole = normalizeRoleKey(roleKey);
                        return (
                          <span
                            key={index}
                            style={{
                              padding: '2px 6px',
                              borderRadius: '8px',
                              fontSize: '11px',
                              fontWeight: 'bold',
                              backgroundColor: normalizedRole === 'admin' ? '#dc3545' : '#6c757d',
                              color: 'white'
                            }}
                          >
                            {displayName}
                          </span>
                        );
                      }) : 'No roles'}
                    </div>
                  </td>
                  <td style={{ padding: '12px' }}>
                    <span style={{
                      padding: '3px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      backgroundColor: user.approvalStatus === 'approved' ? '#e8f5e8' :
                                     user.approvalStatus === 'denied' ? '#ffeaea' : '#fff3cd',
                      color: getStatusColor(user.approvalStatus)
                    }}>
                      {user.approvalStatus?.toUpperCase() || 'PENDING'}
                    </span>
                  </td>
                  <td style={{ padding: '12px', fontSize: '14px', color: '#d1d5db' }}>
                    {formatDate(user.lastSignedIn)}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <div style={{ fontSize: '12px' }}>
                      <div style={{ 
                        color: user.assistantAccessMode === 'all' ? '#10b981' : 
                               user.assistantAccessMode === 'specific' ? '#3b82f6' : '#6b7280',
                        fontWeight: 'bold'
                      }}>
                        {getAssistantAccessSummary(user)}
                      </div>
                      {user.assistantAccessMode === 'specific' && safeArrayConversion(user.accessibleAssistantIds, []).length > 0 && (
                        <div style={{ color: '#9ca3af', marginTop: '2px' }}>
                          IDs: {safeArrayConversion(user.accessibleAssistantIds, []).slice(0, 2).join(', ')}
                          {safeArrayConversion(user.accessibleAssistantIds, []).length > 2 && '...'}
                        </div>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '12px' }}>
                    <div style={{ fontSize: '12px' }}>
                      <div style={{ 
                        color: user.agentAccessMode === 'all' ? '#10b981' : 
                               user.agentAccessMode === 'specific' ? '#3b82f6' : '#6b7280',
                        fontWeight: 'bold'
                      }}>
                        {getAgentAccessSummary(user)}
                      </div>
                      {user.agentAccessMode === 'specific' && safeArrayConversion(user.accessibleAgentNames, []).length > 0 && (
                        <div style={{ color: '#9ca3af', marginTop: '2px' }}>
                          Agents: {safeArrayConversion(user.accessibleAgentNames, []).slice(0, 2).join(', ')}
                          {safeArrayConversion(user.accessibleAgentNames, []).length > 2 && '...'}
                        </div>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '12px' }}>
                    <div style={{ fontSize: '12px' }}>
                      <div style={{ 
                        color: getScenarioAccessSummary(user) === 'Has Access' ? '#10b981' : '#6b7280',
                        fontWeight: 'bold'
                      }}>
                        {getScenarioAccessSummary(user)}
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '12px' }}>
                    <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                      <button
                        onClick={async () => {
                          try {
                            // Fetch fresh user data
                            const freshUser = await api.user.findOne(user.id, {
                              select: {
                                id: true,
                                email: true,
                                firstName: true,
                                lastName: true,
                                institution: true,
                                institutionRole: true,
                                cohort: { id: true, name: true, displayName: true },
                                activeCohorts: true,
                                roles: { key: true, name: true },
                                approvalStatus: true,
                                registrationCompletedAt: true,
                                createdAt: true,
                                lastSignedIn: true,
                                assistantAccessMode: true,
                                accessibleAssistantIds: true,
                                assistantAccessNotes: true,
                                agentAccessMode: true,
                                accessibleAgentNames: true,
                                agentAccessNotes: true,
                                adminNotes: { markdown: true }
                              }
                            });
                            setSelectedUser(freshUser);
                            setShowUserModal(true);
                          } catch (error) {
                            console.error('Failed to fetch fresh user data:', error);
                            // Fallback to cached data
                            setSelectedUser(user);
                            setShowUserModal(true);
                          }
                        }}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: '#007bff',
                          color: 'white',
                          border: 'none',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        View
                      </button>
                      <button
                        onClick={() => openEditModal(user)}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: '#6f42c1',
                          color: 'white',
                          border: 'none',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Edit
                      </button>
                      {user.approvalStatus === 'pending' && (
                        <>
                          <button
                            onClick={() => handleUserAction('approve', user.id)}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: '#28a745',
                              color: 'white',
                              border: 'none',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => confirmActionHandler('deny', user.id)}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: '#dc3545',
                              color: 'white',
                              border: 'none',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            Deny
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => confirmActionHandler('delete', user.id)}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {!fetching && (!users || users.length === 0) && (
          <div style={{ padding: '40px', textAlign: 'center', color: '#d1d5db' }}>
            No users found matching the current filters.
          </div>
        )}
      </div>

      {/* Pagination */}
      {users && (users.hasNextPage || users.hasPreviousPage) && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          gap: '10px', 
          marginTop: '20px' 
        }}>
          {users.hasPreviousPage && (
            <button
              onClick={() => setCurrentPage({ cursor: users.startCursor, direction: 'backward' })}
              style={{
                padding: '8px 16px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Previous Page
            </button>
          )}
          {users.hasNextPage && (
            <button
              onClick={() => setCurrentPage({ cursor: users.endCursor, direction: 'forward' })}
              style={{
                padding: '8px 16px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Next Page
            </button>
          )}
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '30px',
            maxWidth: '400px',
            width: '100%',
            margin: '20px'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '15px' }}>Confirm Action</h3>
            <p style={{ marginBottom: '25px', color: '#666' }}>
              Are you sure you want to {confirmAction?.action} 
              {confirmAction?.userId ? ' this user' : ` ${selectedUsers.size} selected users`}?
              {confirmAction?.action === 'deny' && ' This action cannot be easily undone.'}
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowConfirmDialog(false)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={executeConfirmedAction}
                style={{
                  padding: '8px 16px',
                  backgroundColor: confirmAction?.action === 'deny' ? '#dc3545' : '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User View Modal */}
      {showUserModal && selectedUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#1e1e1e',
            borderRadius: '12px',
            border: '1px solid #333',
            padding: '30px',
            maxWidth: '800px',
            width: '95%',
            maxHeight: '90vh',
            overflow: 'auto',
            color: '#ffffff'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
              <h2 style={{ margin: 0, color: '#ffffff', fontSize: '24px' }}>User Details</h2>
              <button
                onClick={() => setShowUserModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#ffffff',
                  padding: '5px'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '25px' }}>
              {/* Basic Information */}
              <div style={{ backgroundColor: '#2a2a2a', padding: '20px', borderRadius: '8px', border: '1px solid #333' }}>
                <h3 style={{ marginTop: 0, marginBottom: '15px', color: '#3b82f6' }}>Basic Information</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div>
                    <strong style={{ color: '#d1d5db' }}>Email:</strong>
                    <div style={{ color: '#ffffff', marginTop: '2px' }}>{selectedUser.email}</div>
                  </div>
                  <div>
                    <strong style={{ color: '#d1d5db' }}>Name:</strong>
                    <div style={{ color: '#ffffff', marginTop: '2px' }}>
                      {[selectedUser.firstName, selectedUser.lastName].filter(Boolean).join(' ') || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <strong style={{ color: '#d1d5db' }}>Approval Status:</strong>
                    <div style={{ marginTop: '2px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        backgroundColor: selectedUser.approvalStatus === 'approved' ? '#e8f5e8' :
                                       selectedUser.approvalStatus === 'denied' ? '#ffeaea' : '#fff3cd',
                        color: getStatusColor(selectedUser.approvalStatus)
                      }}>
                        {selectedUser.approvalStatus?.toUpperCase() || 'PENDING'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <strong style={{ color: '#d1d5db' }}>Created:</strong>
                    <div style={{ color: '#ffffff', marginTop: '2px' }}>{formatDate(selectedUser.createdAt)}</div>
                  </div>
                  <div>
                    <strong style={{ color: '#d1d5db' }}>Last Signed In:</strong>
                    <div style={{ color: '#ffffff', marginTop: '2px' }}>{formatDate(selectedUser.lastSignedIn)}</div>
                  </div>
                </div>
              </div>

              {/* Institution & Roles */}
              <div style={{ backgroundColor: '#2a2a2a', padding: '20px', borderRadius: '8px', border: '1px solid #333' }}>
                <h3 style={{ marginTop: 0, marginBottom: '15px', color: '#3b82f6' }}>Institution & Roles</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div>
                    <strong style={{ color: '#d1d5db' }}>Institution:</strong>
                    <div style={{ color: '#ffffff', marginTop: '2px' }}>
                      {selectedUser.institution || 'No institution assigned'}
                    </div>
                  </div>
                  <div>
                    <strong style={{ color: '#d1d5db' }}>Cohort:</strong>
                    <div style={{ color: '#ffffff', marginTop: '2px' }}>
                      {selectedUser.cohort ? `${selectedUser.cohort.displayName || selectedUser.cohort.name}` : 'No cohort assigned'}
                    </div>
                  </div>
                  <div>
                    <strong style={{ color: '#d1d5db' }}>Active Cohorts:</strong>
                    <div style={{ color: '#ffffff', marginTop: '2px' }}>
                      {selectedUser.activeCohorts && selectedUser.activeCohorts.length > 0 
                        ? selectedUser.activeCohorts.join(', ') 
                        : 'No active cohorts'}
                    </div>
                  </div>
                  <div>
                    <strong style={{ color: '#d1d5db' }}>System Roles:</strong>
                    <div style={{ marginTop: '5px', display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                      {selectedUser.roles?.map((role, index) => {
                        const roleKey = typeof role === 'string' ? role : role?.key || role?.name || 'unknown';
                        const displayName = typeof role === 'string' ? role : role?.name || role?.key || 'unknown';
                        const normalizedRole = normalizeRoleKey(roleKey);
                        return (
                          <span
                            key={index}
                            style={{
                              padding: '3px 8px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: 'bold',
                              backgroundColor: normalizedRole === 'admin' ? '#dc3545' : '#6c757d',
                              color: 'white'
                            }}
                          >
                            {displayName}
                          </span>
                        );
                      }) || <span style={{ color: '#6b7280' }}>No roles assigned</span>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Assistant Access */}
              <div style={{ backgroundColor: '#2a2a2a', padding: '20px', borderRadius: '8px', border: '1px solid #333' }}>
                <h3 style={{ marginTop: 0, marginBottom: '15px', color: '#3b82f6' }}>Assistant Access</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div>
                    <strong style={{ color: '#d1d5db' }}>Access Mode:</strong>
                    <div style={{ 
                      color: selectedUser.assistantAccessMode === 'all' ? '#10b981' : 
                             selectedUser.assistantAccessMode === 'specific' ? '#3b82f6' : '#6b7280',
                      fontWeight: 'bold',
                      marginTop: '2px'
                    }}>
                      {selectedUser.assistantAccessMode || 'none'}
                    </div>
                  </div>
                  {selectedUser.assistantAccessMode === 'specific' && (
                    <div>
                      <strong style={{ color: '#d1d5db' }}>Accessible Assistant IDs:</strong>
                      <div style={{ color: '#ffffff', marginTop: '2px', fontSize: '12px' }}>
                        {selectedUser.accessibleAssistantIds?.join(', ') || 'None'}
                      </div>
                    </div>
                  )}
                  {selectedUser.assistantAccessNotes && (
                    <div>
                      <strong style={{ color: '#d1d5db' }}>Notes:</strong>
                      <div style={{ color: '#ffffff', marginTop: '2px', fontSize: '12px' }}>
                        {selectedUser.assistantAccessNotes}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Agent Access */}
              <div style={{ backgroundColor: '#2a2a2a', padding: '20px', borderRadius: '8px', border: '1px solid #333' }}>
                <h3 style={{ marginTop: 0, marginBottom: '15px', color: '#3b82f6' }}>Agent Access</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div>
                    <strong style={{ color: '#d1d5db' }}>Access Mode:</strong>
                    <div style={{ 
                      color: selectedUser.agentAccessMode === 'all' ? '#10b981' : 
                             selectedUser.agentAccessMode === 'specific' ? '#3b82f6' : '#6b7280',
                      fontWeight: 'bold',
                      marginTop: '2px'
                    }}>
                      {selectedUser.agentAccessMode || 'none'}
                    </div>
                  </div>
                  {selectedUser.agentAccessMode === 'specific' && (
                    <div>
                      <strong style={{ color: '#d1d5db' }}>Accessible Agent Names:</strong>
                      <div style={{ color: '#ffffff', marginTop: '2px', fontSize: '12px' }}>
                        {selectedUser.accessibleAgentNames?.join(', ') || 'None'}
                      </div>
                    </div>
                  )}
                  {selectedUser.agentAccessNotes && (
                    <div>
                      <strong style={{ color: '#d1d5db' }}>Notes:</strong>
                      <div style={{ color: '#ffffff', marginTop: '2px', fontSize: '12px' }}>
                        {selectedUser.agentAccessNotes}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Admin Notes */}
              {selectedUser.adminNotes?.markdown && (
                <div style={{ backgroundColor: '#2a2a2a', padding: '20px', borderRadius: '8px', border: '1px solid #333', gridColumn: '1 / -1' }}>
                  <h3 style={{ marginTop: 0, marginBottom: '15px', color: '#3b82f6' }}>Admin Notes</h3>
                  <div style={{ 
                    backgroundColor: '#1a1a1a', 
                    padding: '15px', 
                    borderRadius: '6px', 
                    border: '1px solid #333',
                    color: '#ffffff',
                    whiteSpace: 'pre-wrap',
                    fontSize: '14px',
                    lineHeight: '1.4'
                  }}>
                    {selectedUser.adminNotes.markdown}
                  </div>
                </div>
              )}
            </div>

            <div style={{ marginTop: '25px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={() => openEditModal(selectedUser)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Edit User
              </button>
              <button
                onClick={() => setShowUserModal(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Password Reset Modal */}
      {showPasswordResetModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1002
        }}>
          <div style={{
            backgroundColor: '#1e1e1e',
            borderRadius: '12px',
            border: '2px solid #fd7e14',
            padding: '30px',
            maxWidth: '600px',
            width: '95%',
            color: '#ffffff'
          }}>
            <h2 style={{ margin: '0 0 20px 0', color: '#fd7e14', fontSize: '24px' }}>
              Send Password Reset Emails
            </h2>

            <div style={{ 
              padding: '15px',
              backgroundColor: '#2a1a0f',
              border: '1px solid #fd7e14',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <p style={{ margin: '0 0 10px 0', fontSize: '16px', fontWeight: '500' }}>
                You are about to send password reset emails to <strong>{selectedUsers.size}</strong> selected user{selectedUsers.size !== 1 ? 's' : ''}.
              </p>
              <p style={{ margin: 0, fontSize: '14px', color: '#ffc107' }}>
                ⚠️ Warning: This will invalidate their current passwords and send them instructions to create new ones.
              </p>
            </div>

            <div style={{ marginBottom: '25px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '500',
                color: '#d1d5db',
                fontSize: '14px'
              }}>
                Custom Message (Optional)
              </label>
              <textarea
                value={customResetMessage}
                onChange={(e) => setCustomResetMessage(e.target.value)}
                placeholder="Add a custom message to include in the password reset email..."
                rows={4}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  backgroundColor: '#2a2a2a',
                  color: '#ffffff',
                  fontSize: '14px',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
              <p style={{ 
                margin: '8px 0 0 0', 
                fontSize: '12px', 
                color: '#9ca3af' 
              }}>
                This message will be included in the password reset email sent to users.
              </p>
            </div>

            <div style={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              gap: '12px'
            }}>
              <button
                onClick={() => {
                  setShowPasswordResetModal(false);
                  setCustomResetMessage('');
                }}
                disabled={resetting}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: resetting ? 'not-allowed' : 'pointer',
                  fontWeight: '500',
                  fontSize: '14px',
                  opacity: resetting ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkPasswordResetConfirm}
                disabled={resetting}
                style={{
                  padding: '10px 20px',
                  backgroundColor: resetting ? '#6c757d' : '#fd7e14',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: resetting ? 'not-allowed' : 'pointer',
                  fontWeight: '500',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: resetting ? 0.6 : 1
                }}
              >
                {resetting && (
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid #ffffff',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                )}
                {resetting ? 'Sending...' : 'Send Reset Emails'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Add To Cohort Modal */}
      {showBulkCohortModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1002
        }}>
          <div style={{
            backgroundColor: '#1e1e1e',
            borderRadius: '12px',
            border: '2px solid #7c3aed',
            padding: '30px',
            maxWidth: '600px',
            width: '95%',
            color: '#ffffff'
          }}>
            <h2 style={{ margin: '0 0 20px 0', color: '#7c3aed', fontSize: '24px' }}>
              Bulk Add Users To Cohort
            </h2>

            <div style={{
              padding: '15px',
              backgroundColor: '#281a40',
              border: '1px solid #7c3aed',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <p style={{ margin: 0, fontSize: '15px' }}>
                You are about to add <strong>{selectedUsers.size}</strong> selected user{selectedUsers.size !== 1 ? 's' : ''} to a cohort.
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
                color: '#d1d5db'
              }}>
                Cohort
              </label>
              <select
                value={bulkCohortForm.cohortId}
                onChange={(e) => setBulkCohortForm(prev => ({ ...prev, cohortId: e.target.value }))}
                disabled={bulkCohortSubmitting || allCohortsFetching}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  backgroundColor: '#2a2a2a',
                  color: '#ffffff'
                }}
              >
                <option value="">Select Cohort</option>
                {safeArrayMap(allActiveCohorts, cohort => (
                  <option key={cohort.id} value={cohort.id}>
                    {(cohort.displayName || cohort.name)} - {cohort.institution || 'No institution'}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '25px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
                color: '#d1d5db'
              }}>
                Cohort Role For Selected Users
              </label>
              <select
                value={bulkCohortForm.institutionRole}
                onChange={(e) => setBulkCohortForm(prev => ({ ...prev, institutionRole: e.target.value }))}
                disabled={bulkCohortSubmitting}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  backgroundColor: '#2a2a2a',
                  color: '#ffffff'
                }}
              >
                <option value="student">Student</option>
                <option value="instructor">Instructor</option>
                <option value="administrator">Administrator</option>
                <option value="staff">Staff</option>
                <option value="scheduler">Scheduler</option>
              </select>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button
                onClick={() => setShowBulkCohortModal(false)}
                disabled={bulkCohortSubmitting}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: bulkCohortSubmitting ? 'not-allowed' : 'pointer',
                  fontWeight: '500',
                  opacity: bulkCohortSubmitting ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkAddToCohort}
                disabled={bulkCohortSubmitting || allCohortsFetching}
                style={{
                  padding: '10px 20px',
                  backgroundColor: (bulkCohortSubmitting || allCohortsFetching) ? '#6c757d' : '#7c3aed',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: (bulkCohortSubmitting || allCohortsFetching) ? 'not-allowed' : 'pointer',
                  fontWeight: '500',
                  opacity: (bulkCohortSubmitting || allCohortsFetching) ? 0.6 : 1
                }}
              >
                {bulkCohortSubmitting ? 'Adding...' : 'Add To Cohort'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#1e1e1e',
            borderRadius: '12px',
            border: '1px solid #333',
            padding: '30px',
            maxWidth: '900px',
            width: '95%',
            maxHeight: '90vh',
            overflow: 'auto',
            color: '#ffffff'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
              <h2 style={{ margin: 0, color: '#ffffff', fontSize: '24px' }}>Edit User: {selectedUser.email}</h2>
              <button
                onClick={() => setShowEditModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#ffffff',
                  padding: '5px'
                }}
              >
                ×
              </button>
            </div>

            {notification && (
              <div style={{
                padding: '12px 16px',
                marginBottom: '20px',
                borderRadius: '4px',
                backgroundColor: notification.type === 'error' ? '#2d1b1e' : '#1e2d1b',
                color: notification.type === 'error' ? '#ff4757' : '#10b981',
                border: `1px solid ${notification.type === 'error' ? '#ff4757' : '#10b981'}`
              }}>
                {notification.message}
              </div>
            )}

            {/* Tabs */}
            <div style={{ display: 'flex', marginBottom: '25px', borderBottom: '1px solid #333' }}>
              {['basic', 'institution', 'assistants', 'agents', 'scenarios', 'notes'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    padding: '12px 20px',
                    backgroundColor: activeTab === tab ? '#3b82f6' : 'transparent',
                    color: activeTab === tab ? '#ffffff' : '#d1d5db',
                    border: 'none',
                    borderBottom: activeTab === tab ? '2px solid #3b82f6' : '2px solid transparent',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    textTransform: 'capitalize'
                  }}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            {activeTab === 'basic' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#d1d5db' }}>First Name</label>
                    <input
                      type="text"
                      value={editForm.firstName}
                      onChange={(e) => setEditForm(prev => ({ ...prev, firstName: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #333',
                        borderRadius: '6px',
                        backgroundColor: '#2a2a2a',
                        color: '#ffffff'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#d1d5db' }}>Last Name</label>
                    <input
                      type="text"
                      value={editForm.lastName}
                      onChange={(e) => setEditForm(prev => ({ ...prev, lastName: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #333',
                        borderRadius: '6px',
                        backgroundColor: '#2a2a2a',
                        color: '#ffffff'
                      }}
                    />
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#d1d5db' }}>Email</label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #333',
                      borderRadius: '6px',
                      backgroundColor: '#2a2a2a',
                      color: '#ffffff'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#d1d5db' }}>Approval Status</label>
                  <select
                    value={editForm.approvalStatus}
                    onChange={(e) => setEditForm(prev => ({ ...prev, approvalStatus: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #333',
                      borderRadius: '6px',
                      backgroundColor: '#2a2a2a',
                      color: '#ffffff'
                    }}
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="denied">Denied</option>
                  </select>
                </div>
              </div>
            )}

            {activeTab === 'institution' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#d1d5db' }}>Institution</label>
                  <select
                    value={safeArrayConversion(editForm.institutions, [])[0] || ''}
                    onChange={(e) => setEditForm(prev => ({ 
                      ...prev, 
                      institutions: e.target.value ? [e.target.value] : [],
                      institution: e.target.value 
                    }))}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #333',
                      borderRadius: '6px',
                      backgroundColor: '#2a2a2a',
                      color: '#ffffff'
                    }}
                  >
                    <option value="">Select Institution</option>
                    {safeArrayMap(availableInstitutions, institution => (
                      <option key={institution} value={institution}>{institution}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#d1d5db' }}>Or Create New Institution</label>
                  <input
                    type="text"
                    value={editForm.newInstitution}
                    onChange={(e) => setEditForm(prev => ({ ...prev, newInstitution: e.target.value }))}
                    placeholder="Enter new institution name"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #333',
                      borderRadius: '6px',
                      backgroundColor: '#2a2a2a',
                      color: '#ffffff'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold', color: '#d1d5db' }}>Institution Roles</label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px' }}>
                    {safeArrayMap(institutionRoleOptions, option => (
                      <label key={option.value} style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#d1d5db' }}>
                        <input
                          type="checkbox"
                          checked={safeArrayConversion(editForm.institutionRole, []).includes(option.value)}
                          onChange={(e) => handleInstitutionRoleToggle(option.value, e.target.checked)}
                        />
                        {option.label}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Existing Cohort Selection */}
                <div style={{
                  borderTop: '1px solid #333',
                  paddingTop: '20px'
                }}>
                  <h4 style={{ margin: '0 0 15px 0', color: '#3b82f6', fontSize: '16px' }}>Select Existing Cohort</h4>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#d1d5db' }}>Cohort</label>
                    <select
                      value={editForm.cohortId}
                      onChange={(e) => setEditForm(prev => ({ ...prev, cohortId: e.target.value }))}
                      disabled={cohortsFetching}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #333',
                        borderRadius: '6px',
                        backgroundColor: '#2a2a2a',
                        color: '#ffffff'
                      }}
                    >
                      <option value="">Select Cohort</option>
                      {safeArrayMap(cohorts, cohort => (
                        <option key={cohort.id} value={cohort.id}>
                          {cohort.displayName || cohort.name} ({cohort.cohortType})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Create New Cohort Section */}
                <div style={{
                  borderTop: '1px solid #333',
                  paddingTop: '20px'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '15px' 
                  }}>
                    <h4 style={{ margin: 0, color: '#10b981', fontSize: '16px' }}>Create New Cohort</h4>
                    <button
                      type="button"
                      onClick={() => setShowCreateCohort(!showCreateCohort)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: showCreateCohort ? '#6b7280' : '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      {showCreateCohort ? 'Hide' : 'Show'} Form
                    </button>
                  </div>

                  {showCreateCohort && (
                    <div style={{ 
                      backgroundColor: '#252525',
                      padding: '20px',
                      borderRadius: '8px',
                      border: '1px solid #333',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '15px'
                    }}>
                      {createCohortError && (
                        <div style={{
                          padding: '10px',
                          backgroundColor: '#2d1b1e',
                          color: '#ff4757',
                          border: '1px solid #ff4757',
                          borderRadius: '4px',
                          fontSize: '14px'
                        }}>
                          Error creating cohort: {createCohortError.message}
                        </div>
                      )}

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#d1d5db' }}>
                            Cohort Name <span style={{ color: '#ff4757' }}>*</span>
                          </label>
                          <input
                            type="text"
                            value={createCohortForm.name}
                            onChange={(e) => setCreateCohortForm(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="e.g., Fall 2024 EMT Basic"
                            style={{
                              width: '100%',
                              padding: '8px',
                              border: '1px solid #333',
                              borderRadius: '4px',
                              backgroundColor: '#1a1a1a',
                              color: '#ffffff'
                            }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#d1d5db' }}>Display Name</label>
                          <input
                            type="text"
                            value={createCohortForm.displayName}
                            onChange={(e) => setCreateCohortForm(prev => ({ ...prev, displayName: e.target.value }))}
                            placeholder="Optional display name"
                            style={{
                              width: '100%',
                              padding: '8px',
                              border: '1px solid #333',
                              borderRadius: '4px',
                              backgroundColor: '#1a1a1a',
                              color: '#ffffff'
                            }}
                          />
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#d1d5db' }}>
                            Cohort Type <span style={{ color: '#ff4757' }}>*</span>
                          </label>
                          <select
                            value={createCohortForm.cohortType}
                            onChange={(e) => setCreateCohortForm(prev => ({ ...prev, cohortType: e.target.value }))}
                            style={{
                              width: '100%',
                              padding: '8px',
                              border: '1px solid #333',
                              borderRadius: '4px',
                              backgroundColor: '#1a1a1a',
                              color: '#ffffff'
                            }}
                          >
                            <option value="EMT_Basic">EMT Basic</option>
                            <option value="EMT_Advanced">EMT Advanced</option>
                            <option value="Paramedic">Paramedic</option>
                            <option value="Nursing">Nursing</option>
                            <option value="Field_Internship">Field Internship</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#d1d5db' }}>
                            Academic Year <span style={{ color: '#ff4757' }}>*</span>
                          </label>
                          <input
                            type="text"
                            value={createCohortForm.academicYear}
                            onChange={(e) => setCreateCohortForm(prev => ({ ...prev, academicYear: e.target.value }))}
                            placeholder="e.g., 2024-2025"
                            style={{
                              width: '100%',
                              padding: '8px',
                              border: '1px solid #333',
                              borderRadius: '4px',
                              backgroundColor: '#1a1a1a',
                              color: '#ffffff'
                            }}
                          />
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#d1d5db' }}>
                            Start Date <span style={{ color: '#ff4757' }}>*</span>
                          </label>
                          <input
                            type="date"
                            value={createCohortForm.startDate}
                            onChange={(e) => setCreateCohortForm(prev => ({ ...prev, startDate: e.target.value }))}
                            style={{
                              width: '100%',
                              padding: '8px',
                              border: '1px solid #333',
                              borderRadius: '4px',
                              backgroundColor: '#1a1a1a',
                              color: '#ffffff'
                            }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#d1d5db' }}>
                            End Date <span style={{ color: '#ff4757' }}>*</span>
                          </label>
                          <input
                            type="date"
                            value={createCohortForm.endDate}
                            onChange={(e) => setCreateCohortForm(prev => ({ ...prev, endDate: e.target.value }))}
                            style={{
                              width: '100%',
                              padding: '8px',
                              border: '1px solid #333',
                              borderRadius: '4px',
                              backgroundColor: '#1a1a1a',
                              color: '#ffffff'
                            }}
                          />
                        </div>
                      </div>

                      <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#d1d5db' }}>Description</label>
                        <textarea
                          value={createCohortForm.description}
                          onChange={(e) => setCreateCohortForm(prev => ({ ...prev, description: e.target.value }))}
                          placeholder="Optional cohort description"
                          rows={3}
                          style={{
                            width: '100%',
                            padding: '8px',
                            border: '1px solid #333',
                            borderRadius: '4px',
                            backgroundColor: '#1a1a1a',
                            color: '#ffffff',
                            resize: 'vertical'
                          }}
                        />
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '15px' }}>
                        <button
                          type="button"
                          onClick={() => setShowCreateCohort(false)}
                          disabled={creatingCohort}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: creatingCohort ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            opacity: creatingCohort ? 0.6 : 1
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleCreateCohort}
                          disabled={creatingCohort}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: creatingCohort ? '#6c757d' : '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: creatingCohort ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          {creatingCohort && (
                            <div style={{
                              width: '14px',
                              height: '14px',
                              border: '2px solid #ffffff',
                              borderTop: '2px solid transparent',
                              borderRadius: '50%',
                              animation: 'spin 1s linear infinite'
                            }}></div>
                          )}
                          {creatingCohort ? 'Creating...' : 'Create Cohort'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'assistants' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold', color: '#d1d5db' }}>Assistant Access Mode</label>
                  <div style={{ display: 'flex', gap: '15px' }}>
                    {['none', 'specific', 'all'].map(mode => (
                      <label key={mode} style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#d1d5db' }}>
                        <input
                          type="radio"
                          name="assistantAccessMode"
                          value={mode}
                          checked={editForm.assistantAccessMode === mode}
                          onChange={(e) => setEditForm(prev => ({ ...prev, assistantAccessMode: e.target.value }))}
                        />
                        {mode.charAt(0).toUpperCase() + mode.slice(1)}
                      </label>
                    ))}
                  </div>
                </div>

                {editForm.assistantAccessMode === 'specific' && (
                  <div>
                    <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold', color: '#d1d5db' }}>Select Assistants</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '300px', overflowY: 'auto' }}>
                      {safeArrayMap(assistants, assistant => (
                        <label key={assistant.id} style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '10px', 
                          padding: '10px',
                          backgroundColor: '#2a2a2a',
                          borderRadius: '6px',
                          color: '#d1d5db'
                        }}>
                          <input
                            type="checkbox"
                            checked={safeArrayConversion(editForm.accessibleAssistantIds, []).includes(assistant.id)}
                            onChange={(e) => handleAssistantToggle(assistant.id, e.target.checked)}
                          />
                          <div>
                            <div style={{ fontWeight: 'bold' }}>{assistant.name}</div>
                            {assistant.description && (
                              <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>
                                {assistant.description}
                              </div>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'agents' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {/* Individual Agent Access */}
                <div style={{
                  backgroundColor: '#2a2a2a',
                  padding: '20px',
                  borderRadius: '8px',
                  border: '1px solid #333'
                }}>
                  <h3 style={{ marginTop: 0, marginBottom: '15px', color: '#3b82f6', fontSize: '18px' }}>Individual Agent Access</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {availableAgents.map(agent => (
                      <label key={agent.type} style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '10px', 
                        padding: '15px',
                        backgroundColor: '#1a1a1a',
                        borderRadius: '8px',
                        border: '1px solid #333',
                        color: '#d1d5db'
                      }}>
                        <input
                          type="checkbox"
                          checked={editForm.selectedAgents.includes(agent.type)}
                          onChange={(e) => handleAgentToggle(agent.type, e.target.checked)}
                        />
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{ 
                            width: '12px', 
                            height: '12px', 
                            borderRadius: '50%', 
                            backgroundColor: agent.color 
                          }}></div>
                          <div>
                            <div style={{ fontWeight: 'bold', fontSize: '16px' }}>{agent.name}</div>
                            <div style={{ fontSize: '13px', color: '#9ca3af', marginTop: '2px' }}>
                              {agent.description}
                            </div>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Cohort-Based Agent Access */}
                <div style={{
                  backgroundColor: '#2a2a2a',
                  padding: '20px',
                  borderRadius: '8px',
                  border: '1px solid #333'
                }}>
                  <h3 style={{ marginTop: 0, marginBottom: '15px', color: '#3b82f6', fontSize: '18px' }}>Cohort-Based Agent Access</h3>
                  {selectedUser?.cohortMemberships?.edges?.filter(e => e?.node?.active)?.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {selectedUser.cohortMemberships.edges
                        .filter(e => e?.node?.active)
                        .map((edge, index) => (
                          <div key={index} style={{
                            padding: '15px',
                            backgroundColor: '#1e3a5f',
                            borderRadius: '8px',
                            border: '1px solid #3b82f6'
                          }}>
                            <div style={{ 
                              fontSize: '14px', 
                              fontWeight: 'bold', 
                              color: '#3b82f6',
                              marginBottom: '8px'
                            }}>
                              📚 {edge.node.cohort?.name || 'Unknown Cohort'}
                            </div>
                            <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                              Access granted through cohort membership
                            </div>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <div style={{ 
                      padding: '15px', 
                      textAlign: 'center', 
                      color: '#6b7280',
                      fontSize: '14px'
                    }}>
                      No active cohort memberships
                    </div>
                  )}
                </div>

                <div style={{ 
                  padding: '15px', 
                  backgroundColor: '#1e40af', 
                  borderRadius: '6px', 
                  color: '#dbeafe',
                  fontSize: '13px',
                  lineHeight: '1.5'
                }}>
                  <strong>ℹ️ Combined Agent Access:</strong>
                  <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                    <li>If EITHER individual OR cohort access grants an agent, user has access to it</li>
                    <li>Individual access grants can be modified here</li>
                    <li>Cohort-based access is managed through cohort settings</li>
                    <li>Changes will be applied when you save the user</li>
                  </ul>
                </div>
              </div>
            )}

            {activeTab === 'scenarios' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {/* Individual Access Control */}
                <div style={{
                  backgroundColor: '#2a2a2a',
                  padding: '20px',
                  borderRadius: '8px',
                  border: '1px solid #333'
                }}>
                  <h3 style={{ marginTop: 0, marginBottom: '15px', color: '#3b82f6', fontSize: '18px' }}>Individual Scenario Generator Access</h3>
                  
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px',
                    padding: '15px',
                    backgroundColor: editForm.hasCohortScenarioAccess ? '#1e3a5f' : '#1a1a1a',
                    borderRadius: '8px',
                    border: editForm.hasCohortScenarioAccess ? '1px solid #3b82f6' : '1px solid #333',
                    cursor: editForm.hasCohortScenarioAccess ? 'not-allowed' : 'pointer',
                    color: '#d1d5db',
                    opacity: editForm.hasCohortScenarioAccess ? 0.7 : 1
                  }}>
                    <input
                      type="checkbox"
                      checked={editForm.hasScenarioGeneratorAccess || editForm.hasCohortScenarioAccess}
                      disabled={editForm.hasCohortScenarioAccess}
                      onChange={(e) => setEditForm(prev => ({ ...prev, hasScenarioGeneratorAccess: e.target.checked }))}
                      style={{
                        width: '18px',
                        height: '18px',
                        cursor: editForm.hasCohortScenarioAccess ? 'not-allowed' : 'pointer'
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', fontSize: '16px' }}>Grant Individual Scenario Generator Access</div>
                      <div style={{ fontSize: '13px', color: '#9ca3af', marginTop: '4px' }}>
                        {editForm.hasCohortScenarioAccess 
                          ? 'User already has access via cohort membership (checkbox disabled)'
                          : 'Grants access independent of cohort membership'}
                      </div>
                    </div>
                  </label>
                </div>

                <div style={{
                  backgroundColor: '#2a2a2a',
                  padding: '20px',
                  borderRadius: '8px',
                  border: '1px solid #333'
                }}>
                  <h3 style={{ marginTop: 0, marginBottom: '15px', color: '#14b8a6', fontSize: '18px' }}>QuickQA Email Access</h3>

                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '15px',
                    backgroundColor: '#1a1a1a',
                    borderRadius: '8px',
                    border: '1px solid #333',
                    cursor: 'pointer',
                    color: '#d1d5db'
                  }}>
                    <input
                      type="checkbox"
                      checked={editForm.hasQuickQaEmailAccess === true}
                      onChange={(e) => setEditForm(prev => ({ ...prev, hasQuickQaEmailAccess: e.target.checked }))}
                      style={{
                        width: '18px',
                        height: '18px',
                        cursor: 'pointer'
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', fontSize: '16px' }}>Grant Individual QuickQA Email Intake Access</div>
                      <div style={{ fontSize: '13px', color: '#9ca3af', marginTop: '4px' }}>
                        Allows this registered user to trigger QuickQA processing by email even if their institution-level QuickQA intake toggle is off.
                      </div>
                    </div>
                  </label>
                </div>

                {/* Access Status Summary */}
                <div style={{
                  backgroundColor: '#2a2a2a',
                  padding: '20px',
                  borderRadius: '8px',
                  border: '1px solid #333'
                }}>
                  <h3 style={{ marginTop: 0, marginBottom: '15px', color: '#3b82f6', fontSize: '18px' }}>Current Access Status</h3>
                  
                  {/* Individual Access Status */}
                  <div style={{ marginBottom: '15px' }}>
                    <div style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '6px' }}>Individual Access:</div>
                    <div style={{ 
                      color: editForm.hasScenarioGeneratorAccess ? '#10b981' : '#6b7280',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      {editForm.hasScenarioGeneratorAccess ? '✓ Granted' : '✗ Not Granted'}
                      {editForm.hasScenarioGeneratorAccess && (
                        <span style={{
                          fontSize: '11px',
                          padding: '2px 8px',
                          backgroundColor: '#10b981',
                          color: 'white',
                          borderRadius: '4px',
                          fontWeight: 'normal'
                        }}>INDIVIDUAL</span>
                      )}
                    </div>
                  </div>

                  {/* Cohort Access Status */}
                  <div>
                    <div style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '6px' }}>Cohort-Based Access:</div>
                    <div style={{ 
                      color: editForm.hasCohortScenarioAccess ? '#10b981' : '#6b7280',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      {editForm.hasCohortScenarioAccess ? '✓ Has Access' : '✗ No Access'}
                      {editForm.hasCohortScenarioAccess && (
                        <span style={{
                          fontSize: '11px',
                          padding: '2px 8px',
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          borderRadius: '4px',
                          fontWeight: 'normal'
                        }}>VIA COHORT</span>
                      )}
                    </div>
                  </div>

                  {/* Show which cohorts grant access */}
                  {editForm.hasCohortScenarioAccess && selectedUser?.cohortMemberships?.edges?.some(e => e?.node?.active && e?.node?.hasScenarioGeneratorAccess) && (
                    <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #333' }}>
                      <h5 style={{ margin: '0 0 8px 0', color: '#9ca3af', fontSize: '12px' }}>Access Granted By:</h5>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {selectedUser.cohortMemberships.edges
                          .filter(e => e?.node?.active && e?.node?.hasScenarioGeneratorAccess)
                          .map((edge, index) => (
                            <div key={index} style={{
                              fontSize: '12px',
                              color: '#3b82f6',
                              padding: '6px 10px',
                              backgroundColor: '#1e3a5f',
                              borderRadius: '4px',
                              display: 'inline-block',
                              border: '1px solid #3b82f6'
                            }}>
                              📚 {edge.node.cohort?.name || 'Unknown Cohort'}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
                
                <div style={{ 
                  padding: '15px', 
                  backgroundColor: '#1e40af', 
                  borderRadius: '6px', 
                  color: '#dbeafe',
                  fontSize: '13px',
                  lineHeight: '1.5'
                }}>
                  <strong>ℹ️ About Scenario Generator Access:</strong>
                  <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                    <li><strong>Individual Access:</strong> Grants access regardless of cohort membership</li>
                    <li><strong>Cohort Access:</strong> Access granted through active cohort memberships</li>
                    <li>If a user has cohort access, you cannot remove it here (managed via cohort settings)</li>
                    <li>Individual access can be granted as a standalone permission</li>
                    <li>Changes will be applied when you save the user</li>
                  </ul>
                </div>
              </div>
            )}

            {activeTab === 'subscription' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '10px', color: '#ffffff' }}>Subscription & Scenario Credits</h3>
                
                {/* Account Type */}
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#d1d5db' }}>Account Type</label>
                  <select
                    value={editForm.accountType}
                    onChange={(e) => setEditForm({ ...editForm, accountType: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #333',
                      background: '#2a2a2a',
                      color: '#ffffff'
                    }}
                  >
                    <option value="individual">Individual Subscriber</option>
                    <option value="institutional_admin">Institutional Admin</option>
                    <option value="institutional_student">Institutional Student</option>
                  </select>
                  <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '6px' }}>
                    Individual: Personal subscription with credit limit<br/>
                    Institutional Admin: Manages institution and students<br/>
                    Institutional Student: Assigned by admin, uses institution subscription
                  </p>
                </div>

                {/* Unlimited Scenarios (System Admin Only) */}
                <div style={{
                  padding: '16px',
                  background: '#1a1a1a',
                  border: '2px solid #ff6b6b',
                  borderRadius: '8px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <input
                      type="checkbox"
                      checked={editForm.unlimitedScenarios}
                      onChange={(e) => setEditForm({ ...editForm, unlimitedScenarios: e.target.checked })}
                      style={{ width: '20px', height: '20px' }}
                    />
                    <div>
                      <label style={{ fontWeight: '600', color: '#ff6b6b' }}>🔓 Unlimited Scenarios (System Admin Only)</label>
                      <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                        Grant this user unlimited scenario generation regardless of credits or subscription.
                        This bypasses all credit checks and overrides cohort/institution settings.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Scenario Credits (for non-unlimited users) */}
                {!editForm.unlimitedScenarios && (
                  <>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#d1d5db' }}>Scenario Credits Remaining</label>
                      <input
                        type="number"
                        min="0"
                        value={editForm.scenarioCredits}
                        onChange={(e) => setEditForm({ ...editForm, scenarioCredits: parseInt(e.target.value) || 0 })}
                        style={{
                          width: '100%',
                          padding: '10px',
                          borderRadius: '6px',
                          border: '1px solid #333',
                          background: '#2a2a2a',
                          color: '#ffffff'
                        }}
                      />
                      <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '6px' }}>
                        Current: {selectedUser?.scenarioCredits || 0} credits | 
                        Used this year: {selectedUser?.scenariosUsedThisYear || 0}
                      </p>
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#d1d5db' }}>Credits Reset Date</label>
                      <input
                        type="datetime-local"
                        value={editForm.scenarioCreditsResetDate ? new Date(editForm.scenarioCreditsResetDate).toISOString().slice(0, 16) : ''}
                        onChange={(e) => setEditForm({ ...editForm, scenarioCreditsResetDate: e.target.value ? new Date(e.target.value).toISOString() : null })}
                        style={{
                          width: '100%',
                          padding: '10px',
                          borderRadius: '6px',
                          border: '1px solid #333',
                          background: '#2a2a2a',
                          color: '#ffffff'
                        }}
                      />
                      <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '6px' }}>
                        When credits will automatically renew (typically 1 year from grant date)
                      </p>
                    </div>
                  </>
                )}

                {/* Subscription Status */}
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#d1d5db' }}>Subscription Status</label>
                  <select
                    value={editForm.subscriptionStatus}
                    onChange={(e) => setEditForm({ ...editForm, subscriptionStatus: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #333',
                      background: '#2a2a2a',
                      color: '#ffffff'
                    }}
                  >
                    <option value="free">Free</option>
                    <option value="basic">Basic</option>
                    <option value="premium">Premium</option>
                    <option value="enterprise">Enterprise</option>
                    <option value="expired">Expired</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>

                {/* Access Tier (entitlement-first, additive with cohort access) */}
                <div style={{
                  padding: '16px',
                  background: '#1a1a1a',
                  border: '1px solid #333',
                  borderRadius: '8px'
                }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#d1d5db' }}>
                    Access Tier
                  </label>
                  <select
                    value={editForm.accessTier}
                    onChange={(e) => setEditForm({ ...editForm, accessTier: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #333',
                      background: '#2a2a2a',
                      color: '#ffffff'
                    }}
                  >
                    {ACCESS_TIER_OPTIONS.map((option) => (
                      <option key={option.value || "none"} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>

                  <div style={{ marginTop: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#d1d5db' }}>
                      Tier Source
                    </label>
                    <select
                      value={editForm.accessSource}
                      onChange={(e) => setEditForm({ ...editForm, accessSource: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px',
                        borderRadius: '6px',
                        border: '1px solid #333',
                        background: '#2a2a2a',
                        color: '#ffffff'
                      }}
                    >
                      <option value="manual">Manual</option>
                      <option value="individual_purchase">Individual Purchase</option>
                      <option value="institutional_seat">Institutional Seat</option>
                      <option value="trial">Trial</option>
                    </select>
                  </div>

                  <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input
                      type="checkbox"
                      checked={safeArrayConversion(editForm.accessAddons, []).includes("test_question_writer")}
                      onChange={(e) => {
                        const current = new Set(safeArrayConversion(editForm.accessAddons, []));
                        if (e.target.checked) {
                          current.add("test_question_writer");
                        } else {
                          current.delete("test_question_writer");
                        }
                        setEditForm({ ...editForm, accessAddons: Array.from(current) });
                      }}
                      style={{ width: '16px', height: '16px' }}
                    />
                    <label style={{ color: '#d1d5db', fontSize: '14px' }}>Add-on: Test Question Writer</label>
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#d1d5db' }}>Subscription Expiration</label>
                  <input
                    type="datetime-local"
                    value={editForm.subscriptionExpiration ? new Date(editForm.subscriptionExpiration).toISOString().slice(0, 16) : ''}
                    onChange={(e) => setEditForm({ ...editForm, subscriptionExpiration: e.target.value ? new Date(e.target.value).toISOString() : null })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #333',
                      background: '#2a2a2a',
                      color: '#ffffff'
                    }}
                  />
                </div>

                {/* Quick Actions */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '24px' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setEditForm({ 
                        ...editForm, 
                        scenarioCredits: 30,
                        scenarioCreditsResetDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString()
                      });
                    }}
                    style={{
                      padding: '10px',
                      background: '#2563eb',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    Grant 30 Credits (1 Year)
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditForm({ 
                        ...editForm, 
                        scenarioCredits: (selectedUser?.scenarioCredits || 0) + 10
                      });
                    }}
                    style={{
                      padding: '10px',
                      background: '#059669',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    Add 10 Credits
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'notes' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold', color: '#d1d5db' }}>Admin Notes</label>
                  <textarea
                    value={editForm.adminNotes}
                    onChange={(e) => setEditForm(prev => ({ ...prev, adminNotes: e.target.value }))}
                    placeholder="Add administrative notes about this user..."
                    rows={10}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #333',
                      borderRadius: '6px',
                      backgroundColor: '#2a2a2a',
                      color: '#ffffff',
                      fontSize: '14px',
                      resize: 'vertical',
                      fontFamily: 'monospace'
                    }}
                  />
                </div>
                <div style={{ 
                  padding: '10px', 
                  backgroundColor: '#1e40af', 
                  borderRadius: '6px', 
                  color: '#dbeafe',
                  fontSize: '12px'
                }}>
                  Admin notes are only visible to administrators and can be used to track user status, issues, or other relevant information.
                </div>
              </div>
            )}

            {/* Modal Action Buttons */}
            <div style={{ 
              marginTop: '30px', 
              display: 'flex', 
              justifyContent: 'flex-end', 
              gap: '12px',
              borderTop: '1px solid #333',
              paddingTop: '20px'
            }}>
              <button
                onClick={() => setShowEditModal(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveUser}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '14px'
                }}
              >
                Save Changes
              </button>
              <button
                onClick={() => handleUserAction('sendPasswordSetup', selectedUser.id, { email: selectedUser.email })}
                disabled={!!actionBusy[`sendPasswordSetup:${selectedUser.id}`] || !isPendingPasswordSetup(selectedUser)}
                title={!isPendingPasswordSetup(selectedUser) ? 'Password setup is only for approved users who have not completed setup.' : undefined}
                style={{
                  padding: '10px 20px',
                  backgroundColor: (!isPendingPasswordSetup(selectedUser) || actionBusy[`sendPasswordSetup:${selectedUser.id}`]) ? '#495057' : '#17a2b8',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: (!isPendingPasswordSetup(selectedUser) || actionBusy[`sendPasswordSetup:${selectedUser.id}`]) ? 'not-allowed' : 'pointer',
                  fontWeight: '500',
                  fontSize: '14px',
                  opacity: (!isPendingPasswordSetup(selectedUser) || actionBusy[`sendPasswordSetup:${selectedUser.id}`]) ? 0.75 : 1
                }}
              >
                {actionBusy[`sendPasswordSetup:${selectedUser.id}`] ? 'Sending Setup...' : 'Send Password Setup'}
              </button>
              <button
                onClick={() => handleUserAction('resetPassword', selectedUser.id, { email: selectedUser.email })}
                disabled={!!actionBusy[`resetPassword:${selectedUser.id}`] || !isActiveApprovedUser(selectedUser)}
                title={!isActiveApprovedUser(selectedUser) ? 'Password reset is only for approved users who have completed password setup.' : undefined}
                style={{
                  padding: '10px 20px',
                  backgroundColor: (!isActiveApprovedUser(selectedUser) || actionBusy[`resetPassword:${selectedUser.id}`]) ? '#495057' : '#fd7e14',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: (!isActiveApprovedUser(selectedUser) || actionBusy[`resetPassword:${selectedUser.id}`]) ? 'not-allowed' : 'pointer',
                  fontWeight: '500',
                  fontSize: '14px',
                  opacity: (!isActiveApprovedUser(selectedUser) || actionBusy[`resetPassword:${selectedUser.id}`]) ? 0.75 : 1
                }}
              >
                {actionBusy[`resetPassword:${selectedUser.id}`] ? 'Sending Reset...' : 'Reset Password'}
              </button>
              <button
                onClick={() => handleUserAction('generateTempPassword', selectedUser.id)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6f42c1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '14px'
                }}
              >
                Generate Temp Password
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
